import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LicenseRequired from '../../components/ui/LicenseRequired';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import insuranceService from '../../services/equipmentInsurance';
import equipmentService from '../../services/equipment';
import type { Equipment } from '../../types/equipment';
import type {
  InsuranceCoverType, InsurancePolicy, InsurancePolicyInput,
} from '../../types/equipmentInsurance';
import { COVER_TYPE_LABELS } from '../../utils/equipmentInsurance';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

type Mode = 'new' | 'edit' | 'renew';

interface Props {
  mode: Mode;
}

interface FormState {
  insurer: string;
  policy_number: string;
  cover_type: InsuranceCoverType;
  start_date: string;
  expiry_date: string;
  sum_insured: string;
  excess: string;
  premium: string;
  broker_name: string;
  broker_phone: string;
  broker_email: string;
  notes: string;
}

const EMPTY: FormState = {
  insurer: '', policy_number: '', cover_type: 'comprehensive', start_date: '', expiry_date: '',
  sum_insured: '', excess: '', premium: '', broker_name: '', broker_phone: '', broker_email: '', notes: '',
};

const str = (v: unknown) => (v == null ? '' : String(v));

function fromPolicy(p: InsurancePolicy, mode: Mode): FormState {
  return {
    insurer: p.insurer,
    policy_number: p.policy_number,
    cover_type: p.cover_type,
    // A renewal starts when the old policy ends, and its expiry is typed from
    // the renewal notice — never guessed, since a wrong date silences the
    // reminders for a year.
    start_date: mode === 'renew' ? p.expiry_date : str(p.start_date),
    expiry_date: mode === 'renew' ? '' : p.expiry_date,
    sum_insured: str(p.sum_insured),
    excess: str(p.excess),
    premium: mode === 'renew' ? '' : str(p.premium),
    broker_name: str(p.broker_name),
    broker_phone: str(p.broker_phone),
    broker_email: str(p.broker_email),
    notes: str(p.notes),
  };
}

const amount = (s: string): number | null => {
  const t = s.replace(/[$,\s]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

const inputClass = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1 min-w-0">
      <label htmlFor={id} className="block text-sm font-medium text-neutral-700">{label}</label>
      {children}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}

/**
 * Add, correct or renew a policy. One form for all three: a renewal starts
 * from the old policy with its machines, and only the dates (and usually the
 * premium) change. Saving a renewal freezes the old policy as it was.
 */
export default function InsurancePolicyForm({ mode }: Props) {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('vehicles', ['creator', 'admin']);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [original, setOriginal] = useState<InsurancePolicy | null>(null);
  const [machines, setMachines] = useState<Equipment[] | null>(null);
  const [selected, setSelected] = useState<Map<number, string>>(new Map());
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    equipmentService.list()
      .then(res => {
        if (res.success && res.data) setMachines(res.data.filter(m => m.status !== 'retired'));
        else setLoadError('Could not load the equipment register.');
      })
      .catch(() => setLoadError('Could not load the equipment register.'));
  }, []);

  useEffect(() => {
    if (mode === 'new') {
      const pre = Number(params.get('equipment'));
      if (pre) setSelected(new Map([[pre, '']]));
      return;
    }
    insuranceService.get(Number(id))
      .then(res => {
        if (!res.success || !res.data) { setLoadError('Policy not found.'); return; }
        const p = res.data;
        if (p.status !== 'current') { setLoadError('This policy has been renewed or cancelled, and is kept as it was.'); return; }
        setOriginal(p);
        setForm(fromPolicy(p, mode));
        setSelected(new Map(
          (p.items ?? [])
            .filter(i => mode !== 'renew' || i.equipment_status !== 'retired')
            .map(i => [i.equipment_id, str(i.agreed_value)]),
        ));
      })
      .catch(err => setLoadError(err instanceof Error ? err.message : 'Could not load the policy.'));
  }, [mode, id, params]);

  const set = <K extends keyof FormState>(k: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  // Machines already on the policy first, then the rest by name.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (machines ?? []).filter(m => !q || [m.name, m.make, m.model, m.rego_number, m.serial_number]
      .some(v => v && String(v).toLowerCase().includes(q)));
    return [...list].sort((a, b) =>
      Number(selected.has(b.id)) - Number(selected.has(a.id)) || a.name.localeCompare(b.name));
  }, [machines, search, selected]);

  function toggle(mId: number) {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(mId)) next.delete(mId); else next.set(mId, '');
      return next;
    });
  }

  function setAgreed(mId: number, v: string) {
    setSelected(prev => new Map(prev).set(mId, v));
  }

  async function save() {
    setError(null);
    if (!form.insurer.trim()) return setError('Who is the insurer?');
    if (!form.policy_number.trim()) return setError('The policy number is needed.');
    if (!form.expiry_date) return setError(mode === 'renew' ? 'Enter the new expiry date from the renewal notice.' : 'When does it expire?');
    if (form.start_date && form.start_date > form.expiry_date) return setError('The policy ends before it starts.');
    if (mode === 'renew' && original && form.expiry_date <= original.expiry_date) {
      return setError(`The renewal has to run past ${formatLocalDateInTz(original.expiry_date, orgTz)}.`);
    }
    const money = { sum_insured: amount(form.sum_insured), excess: amount(form.excess), premium: amount(form.premium) };
    if (Object.values(money).some(n => Number.isNaN(n))) return setError('An amount is not a number.');
    const items: { equipment_id: number; agreed_value: number | null }[] = [];
    for (const [equipment_id, v] of selected) {
      const n = amount(v);
      if (Number.isNaN(n)) return setError('An agreed value is not a number.');
      items.push({ equipment_id, agreed_value: n });
    }

    const body: InsurancePolicyInput = {
      insurer: form.insurer.trim(),
      policy_number: form.policy_number.trim(),
      cover_type: form.cover_type,
      start_date: form.start_date || null,
      expiry_date: form.expiry_date,
      ...money,
      broker_name: form.broker_name.trim() || null,
      broker_phone: form.broker_phone.trim() || null,
      broker_email: form.broker_email.trim() || null,
      notes: form.notes.trim() || null,
      items,
    };

    setSaving(true);
    try {
      const res = mode === 'new'
        ? await insuranceService.create(body)
        : mode === 'edit'
          ? await insuranceService.update(Number(id), body)
          : await insuranceService.renew(Number(id), body);
      if (res.success && res.data) navigate(`/equipment/insurance/${res.data.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The policy did not save.');
    } finally {
      setSaving(false);
    }
  }

  const title = mode === 'new' ? 'Add insurance policy' : mode === 'edit' ? 'Edit policy' : 'Renew policy';
  const subtitle = original ? `${original.insurer} ${original.policy_number}` : undefined;

  if (licenseLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title={title} subtitle="Loading…" />
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title={title} />
        <LicenseRequired moduleName="Vehicles (Creator or Admin)" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader title={title} subtitle={subtitle} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {loadError ? (
          <p className="text-sm text-red-600">{loadError}</p>
        ) : mode !== 'new' && !original ? (
          <p className="text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : (
          <>
            {mode === 'renew' && original && (
              <p className="text-sm text-neutral-700 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                The current policy ends {formatLocalDateInTz(original.expiry_date, orgTz)}. Enter the new expiry from the
                renewal notice and change anything else that changed. The old policy is kept as it was.
              </p>
            )}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Policy</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="ins-insurer" label="Insurer">
                  <input id="ins-insurer" className={inputClass} value={form.insurer} onChange={set('insurer')} placeholder="NTI, CGU, Elders…" />
                </Field>
                <Field id="ins-number" label="Policy number">
                  <input id="ins-number" className={inputClass} value={form.policy_number} onChange={set('policy_number')} />
                </Field>
                <Field id="ins-cover" label="Cover">
                  <select id="ins-cover" className={inputClass} value={form.cover_type} onChange={set('cover_type')}>
                    {(Object.keys(COVER_TYPE_LABELS) as InsuranceCoverType[]).map(k => (
                      <option key={k} value={k}>{COVER_TYPE_LABELS[k]}</option>
                    ))}
                  </select>
                </Field>
                <div className="hidden sm:block" />
                <Field id="ins-start" label="Starts">
                  <input id="ins-start" type="date" className={inputClass} value={form.start_date} onChange={set('start_date')} />
                </Field>
                <Field id="ins-expiry" label="Expires" hint="Reminders go out a month and a week before this date.">
                  <input id="ins-expiry" type="date" className={inputClass} value={form.expiry_date} onChange={set('expiry_date')} />
                </Field>
                <Field id="ins-sum" label="Sum insured">
                  <input id="ins-sum" inputMode="decimal" className={inputClass} value={form.sum_insured} onChange={set('sum_insured')} />
                </Field>
                <Field id="ins-excess" label="Excess">
                  <input id="ins-excess" inputMode="decimal" className={inputClass} value={form.excess} onChange={set('excess')} />
                </Field>
                <Field id="ins-premium" label="Premium">
                  <input id="ins-premium" inputMode="decimal" className={inputClass} value={form.premium} onChange={set('premium')} />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Broker</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="ins-broker" label="Name">
                  <input id="ins-broker" className={inputClass} value={form.broker_name} onChange={set('broker_name')} />
                </Field>
                <Field id="ins-broker-phone" label="Phone">
                  <input id="ins-broker-phone" type="tel" className={inputClass} value={form.broker_phone} onChange={set('broker_phone')} />
                </Field>
                <Field id="ins-broker-email" label="Email">
                  <input id="ins-broker-email" type="email" className={inputClass} value={form.broker_email} onChange={set('broker_email')} />
                </Field>
                <div className="sm:col-span-2">
                  <Field id="ins-notes" label="Notes">
                    <textarea id="ins-notes" rows={3} className={inputClass} value={form.notes} onChange={set('notes')} />
                  </Field>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Machines covered ({selected.size})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    aria-label="Find a machine"
                    className={`${inputClass} pl-9`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Name, make, rego or serial"
                  />
                </div>
                {machines === null ? (
                  <p className="text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
                ) : visible.length === 0 ? (
                  <p className="text-sm text-neutral-500">No machines match.</p>
                ) : (
                  <ul className="divide-y divide-neutral-100 max-h-[28rem] overflow-y-auto">
                    {visible.map(m => {
                      const on = selected.has(m.id);
                      return (
                        <li key={m.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                          <label className="flex items-start gap-2 min-w-0 flex-1 cursor-pointer">
                            <input type="checkbox" className="mt-1" checked={on} onChange={() => toggle(m.id)} />
                            <span className="min-w-0">
                              <span className="block text-sm text-neutral-800 break-words">{m.name}</span>
                              <span className="block text-xs text-neutral-500 break-words">
                                {[m.make, m.model, m.rego_number].filter(Boolean).join(' · ') || m.equipment_type}
                              </span>
                            </span>
                          </label>
                          {on && (
                            <input
                              aria-label={`Agreed value for ${m.name}`}
                              inputMode="decimal"
                              placeholder="Agreed value"
                              className="w-36 rounded-lg border border-neutral-300 px-2 py-1 text-sm"
                              value={selected.get(m.id) ?? ''}
                              onChange={e => setAgreed(m.id, e.target.value)}
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex flex-wrap gap-2">
              <Button isLoading={saving} onClick={save}>
                {mode === 'renew' ? 'Save the renewal' : 'Save policy'}
              </Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>Back</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
